import { put, select, takeEvery } from "redux-saga/effects"
import {
  axiosPut,
  delPointsAxiosWithParams,
  get,
  getPointsAxiosWithParams,
  post,
  postImageAxios,
  putPointsAxios,
  postPointsAxios,
} from "helpers/api_helper"
import { getFormDataFromJSON, showToast } from "utils"
import {
  showSavingModal,
  hideSavingModal,
  putMenuItemSuccess,
  setSelectedFeature,
  setIsOutdated,
} from "../actions"
import {
  addQuestionnaireFailed,
  addQuestionnaireSuccess,
  deleteQuestionnaireFailed,
  deleteQuestionnaireSubmissionFailed,
  deleteQuestionnaireSubmissionSuccess,
  deleteQuestionnaireSuccess,
  getQuestionnaireFailed,
  getQuestionnaireListFailed,
  getQuestionnaireListSuccess,
  getQuestionnaireReplyFailed,
  getQuestionnaireReplySuccess,
  getQuestionnaireScoresFailed,
  getQuestionnaireScoresSuccess,
  getQuestionnaireSuccess,
  hideDeleteQuestionnaireModal,
  postQuestionnaireDesignFailed,
  postQuestionnaireDesignSuccess,
  postQuestionnaireResultFailed,
  postQuestionnaireResultSuccess,
  updateQuestionnaireFailed,
  updateQuestionnaireMenuItemFailed,
  updateQuestionnaireMenuItemSuccess,
  updateQuestionnaireOrderFailed,
  updateQuestionnaireOrderSuccess,
  updateQuestionnaireSuccess,
} from "./actions"
import {
  ADD_QUESTIONNAIRE,
  DELETE_QUESTIONNAIRE,
  DELETE_QUESTIONNAIRE_SUBMISSION,
  GET_QUESTIONNAIRE,
  GET_QUESTIONNAIRE_LIST,
  GET_QUESTIONNAIRE_REPLY,
  GET_QUESTIONNAIRE_SCORES,
  POST_QUESTIONNAIRE_DESIGN,
  POST_QUESTIONNAIRE_RESULT,
  UPDATE_QUESTIONNAIRE,
  UPDATE_QUESTIONNAIRE_MENU_ITEM,
  UPDATE_QUESTIONNAIRE_ORDER,
} from "./actionTypes"

function* addQuestionnaire({ payload: params }) {
  try {
    yield put(showSavingModal())
    const questionsResponse = yield postPointsAxios("/questionnaire-question", {
      questions: params.questions,
    })
    const questionnaireResponse = yield postPointsAxios("/questionnaire", {
      ...params.questionnaire,
      questions: questionsResponse.map(question => question.id),
    })
    if (params.questionnaire.menu_feature) {
      const menuItem = yield axiosPut("/admin/menu-item", {
        entity_id: questionnaireResponse.id,
        menu_item_id: questionnaireResponse.menu_item_id,
      })
      yield put(putMenuItemSuccess(menuItem))
      yield put(setSelectedFeature(menuItem))
      yield put(setIsOutdated(true))
    } else {
      const listResponse = yield getPointsAxiosWithParams("/questionnaire", {
        params: {
          event_id: questionnaireResponse.event_id,
          menu_item_id: questionnaireResponse.menu_item_id,
        },
      })
      yield put(getQuestionnaireListSuccess(listResponse))
    }
    yield put(addQuestionnaireSuccess(questionnaireResponse))
    yield put(hideSavingModal())
  } catch (error) {
    yield put(addQuestionnaireFailed(error))
  }
}

function* getQuestionnaire({ payload: params }) {
  try {
    const questionnaireResponse = yield getPointsAxiosWithParams(
      `/questionnaire/${params.id}`
    )
    const scoresResponse = yield get("/admin/questionnaire-scores", {
      params: {
        questionnaire_id: params.id,
        page: 1,
        per_page: 50,
        o: "created_at",
        od: "desc",
      },
    })
    const resultsResponse = yield getPointsAxiosWithParams(
      "/questionnaire-results",
      {
        params: {
          questionnaire_id: params.id,
        },
      }
    )
    const designResponse = yield get("/questionnaire-design", {
      params: {
        questionnaire_id: params.id,
      },
    })
    const destroyed = yield select(state => state.Questionnaire.destroyed)
    if (!destroyed) {
      yield put(
        getQuestionnaireSuccess({
          scores: scoresResponse,
          results: resultsResponse,
          design: designResponse,
          questionnaire: questionnaireResponse,
        })
      )
    }
  } catch (error) {
    yield put(getQuestionnaireFailed(error))
  }
}

function* updateQuestionnaire({ payload: params }) {
  try {
    yield put(showSavingModal())
    const questionIds = []
    for (let question of params.updatedQuestions) {
      const response = yield putPointsAxios(
        `/questionnaire-question/${question.id}`,
        question
      )
      questionIds.push(response.id)
    }
    if (params.newQuestions.length > 0) {
      const newQuestionsResponse = yield postPointsAxios(
        "/questionnaire-question",
        { questions: params.newQuestions }
      )
      newQuestionsResponse.forEach(item => {
        questionIds.push(item.id)
      })
    }
    const questionnaireResponse = yield putPointsAxios(
      `/questionnaire/${params.questionnaire.id}`,
      {
        ...params.questionnaire,
        questions: questionIds,
      },
      {
        params: { menu_item_id: params.questionnaire.menu_item_id },
      }
    )
    yield put(updateQuestionnaireSuccess(questionnaireResponse))
    yield put(hideSavingModal())
  } catch (error) {
    yield put(updateQuestionnaireFailed(error))
  }
}

function* postQuestionnaireResult({ payload: params }) {
  try {
    yield put(showSavingModal())
    const response = yield postPointsAxios("/questionnaire-results", params)
    yield put(postQuestionnaireResultSuccess(response))
    yield put(hideSavingModal())
  } catch (error) {
    yield put(postQuestionnaireResultFailed(error))
  }
}

function* postQuestionnaireDesign({ payload: params }) {
  try {
    yield put(showSavingModal())
    let bgImageResponse = null
    if (params.bgImage) {
      bgImageResponse = yield postImageAxios(
        "/file",
        getFormDataFromJSON(params.bgImage)
      )
    }
    let imageResponse = null
    if (params.image) {
      imageResponse = yield postImageAxios(
        "/file",
        getFormDataFromJSON(params.image)
      )
    }
    yield post("/admin/questionnaire-design", {
      ...params.design,
      ...(bgImageResponse && { bg_image_file: bgImageResponse[0] }),
      ...(imageResponse && { image_file: imageResponse[0] }),
    })
    const designResponse = yield get("/questionnaire-design", {
      params: {
        questionnaire_id: params.design.questionnaire_id,
      },
    })
    yield put(postQuestionnaireDesignSuccess(designResponse))
    yield put(hideSavingModal())
  } catch (error) {
    yield put(postQuestionnaireDesignFailed(error))
  }
}

function* getQuestionnaireList({ payload: params }) {
  try {
    const response = yield getPointsAxiosWithParams("/questionnaire", {
      params,
    })
    const destroyed = yield select(state => state.Questionnaire.listDestroyed)
    if (!destroyed) {
      yield put(getQuestionnaireListSuccess(response))
    }
  } catch (error) {
    yield put(getQuestionnaireListFailed(error))
  }
}

function* deleteQuestionnaire({ payload: params }) {
  try {
    const response = yield delPointsAxiosWithParams(
      `/questionnaire/${params.id}`,
      {
        menu_item_id: params.menu_item_id,
      }
    )
    yield put(deleteQuestionnaireSuccess(response))
    yield put(hideDeleteQuestionnaireModal())
  } catch (error) {
    yield put(deleteQuestionnaireFailed(error))
  }
}

function* updateQuestionnaireOrder({ payload: params }) {
  try {
    const response = yield putPointsAxios(
      `/questionnaire/${params.id}`,
      params,
      {
        params: { menu_item_id: params.menu_item_id },
      }
    )
    yield put(updateQuestionnaireOrderSuccess(response))
  } catch (error) {
    yield put(updateQuestionnaireOrderFailed(error))
  }
}

function* getQuestionnaireReply({ payload: params }) {
  try {
    const response = yield getPointsAxiosWithParams(
      `/questionnaire/${params?.id}/reply`
    )
    yield put(getQuestionnaireReplySuccess(response))
  } catch (error) {
    yield put(getQuestionnaireReplyFailed(error))
  }
}

function* getQuestionnaireScores({ payload: params }) {
  try {
    const response = yield get("/admin/questionnaire-scores", { params })
    yield put(getQuestionnaireScoresSuccess(response))
  } catch (error) {
    yield put(getQuestionnaireScoresFailed(error))
  }
}

function* deleteQuestionnaireSubmission({ payload: params }) {
  try {
    const response = yield postPointsAxios(
      "/delete-questionnaire-submissions",
      params?.values
    )
    if (params?.callback) {
      params?.callback()
    }
    yield put(deleteQuestionnaireSubmissionSuccess(response))
    showToast("success", "Submissions deleted!", "Success")
  } catch (error) {
    yield put(deleteQuestionnaireSubmissionFailed(error))
    showToast("error", error, "Error")
  }
}

function* updateQuestionnaireMenuItem({ payload: params }) {
  try {
    const response = yield putPointsAxios(
      `/questionnaire/${params?.id}`,
      params,
      {
        params: { menu_item_id: params.menu_item_id },
      }
    )
    yield put(setIsOutdated(true))
    yield put(updateQuestionnaireMenuItemSuccess(response))
  } catch ({ response }) {
    yield put(
      updateQuestionnaireMenuItemFailed(
        response?.data?.error || "Something went wrong, please try again!"
      )
    )
  }
}

function* questionnaireSaga() {
  yield takeEvery(ADD_QUESTIONNAIRE, addQuestionnaire)
  yield takeEvery(GET_QUESTIONNAIRE, getQuestionnaire)
  yield takeEvery(UPDATE_QUESTIONNAIRE, updateQuestionnaire)
  yield takeEvery(POST_QUESTIONNAIRE_RESULT, postQuestionnaireResult)
  yield takeEvery(POST_QUESTIONNAIRE_DESIGN, postQuestionnaireDesign)
  yield takeEvery(GET_QUESTIONNAIRE_LIST, getQuestionnaireList)
  yield takeEvery(DELETE_QUESTIONNAIRE, deleteQuestionnaire)
  yield takeEvery(UPDATE_QUESTIONNAIRE_ORDER, updateQuestionnaireOrder)
  yield takeEvery(GET_QUESTIONNAIRE_REPLY, getQuestionnaireReply)
  yield takeEvery(GET_QUESTIONNAIRE_SCORES, getQuestionnaireScores)
  yield takeEvery(
    DELETE_QUESTIONNAIRE_SUBMISSION,
    deleteQuestionnaireSubmission
  )
  yield takeEvery(UPDATE_QUESTIONNAIRE_MENU_ITEM, updateQuestionnaireMenuItem)
}

export default questionnaireSaga
